import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, XCircle, Users } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import {
  useBulkInviteUsers,
  type BulkInviteItem,
  type BulkInviteResponse,
} from '@/api/users';
import { useOrganizations } from '@/api/organizations';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import type { Role } from '@/types/api';

interface ParsedRow {
  rowNumber: number;
  raw: string;
  email: string;
  role: Role;
  fullName: string;
  valid: boolean;
  error?: string;
}

interface BulkInviteModalProps {
  open: boolean;
  onClose: () => void;
}

const ROLE_VALUES: Role[] = ['sysadmin', 'manager', 'staff', 'client_admin', 'client_user'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function looksLikeHeader(cells: string[]): boolean {
  const first = (cells[0] ?? '').trim().toLowerCase();
  return first === 'email' || first.startsWith('email');
}

function parseCsv(input: string): ParsedRow[] {
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return [];

  const firstCells = lines[0]!.split(',').map((c) => c.trim());
  const startIdx = looksLikeHeader(firstCells) ? 1 : 0;

  const rows: ParsedRow[] = [];
  for (let i = startIdx; i < lines.length; i += 1) {
    const raw = lines[i]!;
    const cells = raw.split(',').map((c) => c.trim());
    const email = (cells[0] ?? '').toLowerCase();
    const roleRaw = (cells[1] ?? '').toLowerCase();
    const fullName = cells.slice(2).join(',').trim();

    let valid = true;
    let error: string | undefined;

    if (!email) {
      valid = false;
      error = 'Missing email';
    } else if (!EMAIL_RE.test(email)) {
      valid = false;
      error = 'Invalid email';
    }

    let role: Role = 'client_user';
    if (roleRaw) {
      if ((ROLE_VALUES as string[]).includes(roleRaw)) {
        role = roleRaw as Role;
      } else {
        valid = false;
        error = error ?? `Invalid role "${roleRaw}"`;
      }
    }

    rows.push({
      rowNumber: i + 1,
      raw,
      email,
      role,
      fullName,
      valid,
      error,
    });
  }
  return rows;
}

export function BulkInviteModal({ open, onClose }: BulkInviteModalProps) {
  const orgs = useOrganizations();
  const bulk = useBulkInviteUsers();
  const toast = useToast();

  const [csv, setCsv] = useState('');
  const [orgId, setOrgId] = useState<string>('');
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [response, setResponse] = useState<BulkInviteResponse | null>(null);

  const reset = () => {
    setCsv('');
    setOrgId('');
    setParsed(null);
    setResponse(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleParse = () => {
    const rows = parseCsv(csv);
    setParsed(rows);
    setResponse(null);
  };

  const validRows = useMemo(
    () => (parsed ?? []).filter((r) => r.valid && r.email),
    [parsed],
  );

  const handleSend = async () => {
    if (validRows.length === 0) return;
    const invites: BulkInviteItem[] = validRows.map((r) => ({
      email: r.email,
      role: r.role,
      organization_id:
        r.role === 'client_admin' || r.role === 'client_user' ? orgId || null : null,
    }));
    try {
      const data = await bulk.mutateAsync(invites);
      setResponse(data);
      toast.success(
        'Bulk invite complete',
        `${data.sent} sent, ${data.skipped} skipped, ${data.failed} failed`,
      );
    } catch (e) {
      toast.error('Bulk invite failed', apiError(e));
    }
  };

  const needsOrg = validRows.some(
    (r) => r.role === 'client_admin' || r.role === 'client_user',
  );
  const orgMissing = needsOrg && !orgId;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk invite users"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          {!response && (
            <>
              <Button variant="secondary" onClick={handleParse} disabled={!csv.trim()}>
                Parse
              </Button>
              <Button
                variant="primary"
                onClick={handleSend}
                loading={bulk.isPending}
                disabled={!parsed || validRows.length === 0 || orgMissing}
                className="w-auto"
              >
                Send invites ({validRows.length})
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {!response && (
          <>
            <div>
              <Label htmlFor="bulk-csv">
                CSV (one invite per line) — format: email,role,full_name
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                Format: one invite per line, comma-separated:{' '}
                <code>email@example.com,client_user,Full Name</code>. Role and name are
                optional. Role defaults to <code>client_user</code> if omitted. A header
                row is auto-detected and skipped. All client invites share the
                organization selected below.
              </p>
              <textarea
                id="bulk-csv"
                value={csv}
                onChange={(e) => {
                  setCsv(e.target.value);
                  setParsed(null);
                }}
                rows={8}
                placeholder={'email,role,full_name\nalice@example.com,client_user,Alice Smith\nbob@example.com,client_admin,Bob Jones'}
                className="w-full border border-bb-lilac bg-white px-3 py-2 text-sm font-mono text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-bb-navy/30 focus:border-bb-navy"
              />
            </div>

            <div>
              <Label htmlFor="bulk-org">Organization (for client invites)</Label>
              <Select
                id="bulk-org"
                value={orgId}
                onChange={setOrgId}
                options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
                placeholder="-- None --"
              />
              {orgMissing && (
                <p className="text-xs text-red-600 mt-1">
                  Pick an organization — at least one parsed row is for a client role.
                </p>
              )}
            </div>

            {parsed !== null && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-bb-navy">
                    Preview ({validRows.length} valid / {parsed.length} total)
                  </div>
                  {bulk.isPending && <Spinner size={14} />}
                </div>
                {parsed.length === 0 ? (
                  <div className="text-sm text-slate-500 border border-slate-200 p-4">
                    No rows detected — paste one invite per line.
                  </div>
                ) : (
                  <div className="border border-slate-200 max-h-64 overflow-y-auto custom-scrollbar">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                          <th className="py-2 px-2 font-semibold">#</th>
                          <th className="py-2 px-2 font-semibold">Email</th>
                          <th className="py-2 px-2 font-semibold">Role</th>
                          <th className="py-2 px-2 font-semibold">Name</th>
                          <th className="py-2 px-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.map((row) => (
                          <tr
                            key={row.rowNumber}
                            className={`border-b border-slate-100 ${row.valid ? '' : 'bg-red-50/50'}`}
                          >
                            <td className="py-1.5 px-2 text-slate-500">{row.rowNumber}</td>
                            <td className="py-1.5 px-2 text-slate-800">
                              {row.email || <span className="text-slate-400">—</span>}
                            </td>
                            <td className="py-1.5 px-2">
                              <Badge tone="blue">{row.role}</Badge>
                            </td>
                            <td className="py-1.5 px-2 text-slate-600">
                              {row.fullName || <span className="text-slate-400">—</span>}
                            </td>
                            <td className="py-1.5 px-2">
                              {row.valid ? (
                                <Badge tone="green">
                                  <CheckCircle2 size={12} /> valid
                                </Badge>
                              ) : (
                                <Badge tone="red">
                                  <XCircle size={12} /> {row.error ?? 'invalid'}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {response && (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="border border-slate-200 p-3">
                <div className="text-xs uppercase text-slate-500">Sent</div>
                <div className="text-2xl font-bold text-green-700">{response.sent}</div>
              </div>
              <div className="border border-slate-200 p-3">
                <div className="text-xs uppercase text-slate-500">Skipped</div>
                <div className="text-2xl font-bold text-yellow-700">
                  {response.skipped}
                </div>
              </div>
              <div className="border border-slate-200 p-3">
                <div className="text-xs uppercase text-slate-500">Failed</div>
                <div className="text-2xl font-bold text-red-700">{response.failed}</div>
              </div>
            </div>
            <div className="border border-slate-200 max-h-72 overflow-y-auto custom-scrollbar">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 px-2 font-semibold">Email</th>
                    <th className="py-2 px-2 font-semibold">Result</th>
                    <th className="py-2 px-2 font-semibold">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {response.results.map((r) => {
                    const parsedRow = (parsed ?? []).find((p) => p.email === r.email);
                    return (
                      <tr
                        key={`${r.email}-${r.invite_id ?? r.status}`}
                        className="border-b border-slate-100"
                      >
                        <td className="py-1.5 px-2 text-slate-800">{r.email}</td>
                        <td className="py-1.5 px-2">
                          {r.status === 'sent' && (
                            <Badge tone="green">
                              <CheckCircle2 size={12} /> sent
                            </Badge>
                          )}
                          {r.status === 'skipped' && (
                            <Badge tone="yellow">
                              <AlertCircle size={12} /> skipped
                            </Badge>
                          )}
                          {r.status === 'failed' && (
                            <Badge tone="red">
                              <XCircle size={12} /> failed
                            </Badge>
                          )}
                        </td>
                        <td className="py-1.5 px-2 text-slate-600">
                          {r.reason ?? (parsedRow ? '' : '')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
              <Users size={14} />
              Close this dialog to return to the user list.
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
